// Shared animation timing and interpolation utilities
import { ANIMATION_TIME_STEP } from '../constants/animation';

export interface SectionAnimationData {
  time: number;
  optimal_time: number;
  lat_accel: number;
  long_accel: number;
  optimal_lat_accel: number;
  optimal_long_accel: number;
  speed: number;
  optimal_speed: number;
  latitude: number;
  longitude: number;
  displacement: number;
  index: number;
}

export interface AnimationTiming {
  actualRelativeTime: number[];
  optimalRelativeTime: number[];
  maxSectionDuration: number;
  sectionData: SectionAnimationData[];
  // New upsampled data for smooth animation
  upsampledTimeVector: number[];
  upsampledActualData: SectionAnimationData[];
  upsampledOptimalData: SectionAnimationData[];
}

export interface CurrentAnimationFrame {
  actualIndex: number;
  optimalIndex: number;
  actual: {
    lat_accel: number;
    long_accel: number;
    speed: number;
    optimal_speed: number;
    time: number;
    optimal_time: number;
    latitude: number;
    longitude: number;
  };
  optimal: {
    lat_accel: number;
    long_accel: number;
    speed: number;
    time: number;
    latitude: number;
    longitude: number;
  };
}

/**
 * Linear interpolation between two values
 */
export const lerp = (a: number, b: number, t: number): number => {
  return a + (b - a) * t;
};

/**
 * Find the interpolation parameters for a given time in a time array
 */
export const findInterpolationParams = (timeArray: number[], targetTime: number): { index: number; t: number } => {
  if (timeArray.length === 0) return { index: 0, t: 0 };
  
  // Clamp to bounds
  if (targetTime <= timeArray[0]) return { index: 0, t: 0 };
  if (targetTime >= timeArray[timeArray.length - 1]) return { index: timeArray.length - 1, t: 0 };
  
  // Find the interval
  for (let i = 0; i < timeArray.length - 1; i++) {
    if (targetTime >= timeArray[i] && targetTime <= timeArray[i + 1]) {
      const timeDiff = timeArray[i + 1] - timeArray[i];
      const t = timeDiff === 0 ? 0 : (targetTime - timeArray[i]) / timeDiff;
      return { index: i, t };
    }
  }
  
  return { index: timeArray.length - 1, t: 0 };
};

/**
 * Interpolate a single data point at a specific time
 */
export const interpolateDataPoint = (
  sourceData: SectionAnimationData[],
  sourceTimeArray: number[],
  targetTime: number
): SectionAnimationData => {
  if (sourceData.length === 0) {
    return {
      time: targetTime,
      optimal_time: targetTime,
      lat_accel: 0,
      long_accel: 0,
      optimal_lat_accel: 0,
      optimal_long_accel: 0,
      speed: 0,
      optimal_speed: 0,
      latitude: 0,
      longitude: 0,
      displacement: 0,
      index: 0
    };
  }
  
  const { index, t } = findInterpolationParams(sourceTimeArray, targetTime);
  
  if (index >= sourceData.length - 1 || t === 0) {
    // Return the exact point or the last point
    const point = sourceData[Math.min(index, sourceData.length - 1)];
    return { ...point };
  }
  
  const point1 = sourceData[index];
  const point2 = sourceData[index + 1];
  
  return {
    time: lerp(point1.time, point2.time, t),
    optimal_time: lerp(point1.optimal_time, point2.optimal_time, t),
    lat_accel: lerp(point1.lat_accel, point2.lat_accel, t),
    long_accel: lerp(point1.long_accel, point2.long_accel, t),
    optimal_lat_accel: lerp(point1.optimal_lat_accel, point2.optimal_lat_accel, t),
    optimal_long_accel: lerp(point1.optimal_long_accel, point2.optimal_long_accel, t),
    speed: lerp(point1.speed, point2.speed, t),
    optimal_speed: lerp(point1.optimal_speed, point2.optimal_speed, t),
    latitude: lerp(point1.latitude, point2.latitude, t),
    longitude: lerp(point1.longitude, point2.longitude, t),
    displacement: lerp(point1.displacement, point2.displacement, t),
    index: lerp(point1.index, point2.index, t)
  };
};

/**
 * Create upsampled time vector and data arrays for smooth animation
 */
export const createUpsampledAnimationData = (
  sectionData: SectionAnimationData[],
  actualRelativeTime: number[],
  optimalRelativeTime: number[]
): { 
  upsampledTimeVector: number[]; 
  upsampledActualData: SectionAnimationData[]; 
  upsampledOptimalData: SectionAnimationData[]; 
} => {
  if (sectionData.length === 0 || actualRelativeTime.length === 0) {
    return {
      upsampledTimeVector: [],
      upsampledActualData: [],
      upsampledOptimalData: []
    };
  }
  
  const maxTime = Math.max(
    actualRelativeTime[actualRelativeTime.length - 1],
    optimalRelativeTime[optimalRelativeTime.length - 1]
  );
  
  // Create upsampled time vector with ANIMATION_TIME_STEP spacing
  const upsampledTimeVector: number[] = [];
  for (let t = 0; t <= maxTime; t += ANIMATION_TIME_STEP) {
    upsampledTimeVector.push(t);
  }
  
  // Ensure we include the final time point
  if (upsampledTimeVector[upsampledTimeVector.length - 1] < maxTime) {
    upsampledTimeVector.push(maxTime);
  }
  
  // Create upsampled data arrays
  const upsampledActualData: SectionAnimationData[] = [];
  const upsampledOptimalData: SectionAnimationData[] = [];
  
  for (const targetTime of upsampledTimeVector) {
    // Interpolate actual data
    const actualPoint = interpolateDataPoint(sectionData, actualRelativeTime, targetTime);
    upsampledActualData.push(actualPoint);
    
    // Interpolate optimal data (using optimal time vector)
    const optimalPoint = interpolateDataPoint(sectionData, optimalRelativeTime, targetTime);
    // Override with optimal values
    optimalPoint.lat_accel = optimalPoint.optimal_lat_accel;
    optimalPoint.long_accel = optimalPoint.optimal_long_accel;
    optimalPoint.speed = optimalPoint.optimal_speed;
    upsampledOptimalData.push(optimalPoint);
  }
  
  return {
    upsampledTimeVector,
    upsampledActualData,
    upsampledOptimalData
  };
};

/**
 * Calculate progress through displacement path based on elapsed time
 */
export const calculateDisplacementProgress = (timeVector: number[], elapsedTime: number): number => {
  if (timeVector.length === 0) return 0;
  
  // If elapsed time is beyond the lap's section duration, return 100% progress
  const maxTime = timeVector[timeVector.length - 1];
  if (elapsedTime >= maxTime) {
    return 1.0;
  }
  
  // If elapsed time is before the start, return 0% progress
  if (elapsedTime <= timeVector[0]) {
    return 0;
  }
  
  // Find where in the time vector this elapsed time falls
  for (let i = 0; i < timeVector.length - 1; i++) {
    if (elapsedTime >= timeVector[i] && elapsedTime < timeVector[i + 1]) {
      // Calculate progress as a percentage through the displacement path
      const timeDiff = timeVector[i + 1] - timeVector[i];
      const timeRatio = timeDiff === 0 ? 0 : (elapsedTime - timeVector[i]) / timeDiff;
      const progressAtI = i / (timeVector.length - 1);
      const progressAtNext = (i + 1) / (timeVector.length - 1);
      return progressAtI + timeRatio * (progressAtNext - progressAtI);
    }
  }
  
  return 1.0;
};

/**
 * Extract section data and calculate timing information
 */
export const calculateSectionTiming = (
  startIdx: number,
  endIdx: number,
  lapData: {
    displacement: number[];
    time?: number[] | null;
    lat_accel?: number[] | null;
    long_accel?: number[] | null;
    optimal_lat_accel?: number[] | null;
    optimal_long_accel?: number[] | null;
    speed?: number[] | null;
    optimal_speed?: number[] | null;
    latitude?: number[] | null;
    longitude?: number[] | null;
    optimal_time?: number[] | null;
  }
): AnimationTiming => {
  const minIdx = Math.min(startIdx, endIdx);
  const maxIdx = Math.max(startIdx, endIdx);
  
  const sectionData: SectionAnimationData[] = [];
  for (let i = minIdx; i <= maxIdx; i++) {
    if (i < lapData.displacement.length && 
        i < (lapData.time?.length || 0) && 
        i < (lapData.lat_accel?.length || 0) && 
        i < (lapData.long_accel?.length || 0)) {
      sectionData.push({
        index: i,
        displacement: lapData.displacement[i],
        lat_accel: -(lapData.lat_accel?.[i] || 0),
        long_accel: -(lapData.long_accel?.[i] || 0),
        optimal_lat_accel: -(lapData.optimal_lat_accel?.[i] || 0),
        optimal_long_accel: -(lapData.optimal_long_accel?.[i] || 0),
        time: lapData.time?.[i] || 0,
        optimal_time: lapData.optimal_time?.[i] || lapData.time?.[i] || 0,
        latitude: lapData.latitude?.[i] || 0,
        longitude: lapData.longitude?.[i] || 0,
        speed: lapData.speed?.[i] || 0,
        optimal_speed: lapData.optimal_speed?.[i] || 0
      });
    }
  }
  
  if (sectionData.length === 0) {
    return {
      actualRelativeTime: [],
      optimalRelativeTime: [],
      maxSectionDuration: 3000,
      sectionData: [],
      upsampledTimeVector: [],
      upsampledActualData: [],
      upsampledOptimalData: []
    };
  }
  
  // Create relative time vectors for actual and optimal (both start at 0)
  const actualStartTime = sectionData[0].time;
  const optimalStartTime = sectionData[0].optimal_time;
  
  const actualRelativeTime = sectionData.map(d => d.time - actualStartTime);
  const optimalRelativeTime = sectionData.map(d => d.optimal_time - optimalStartTime);
  
  // Calculate section durations for both actual and optimal
  const actualDurationSeconds = actualRelativeTime[actualRelativeTime.length - 1];
  const optimalDurationSeconds = optimalRelativeTime[optimalRelativeTime.length - 1];
  
  // Convert to milliseconds and ensure minimum duration for visibility
  const actualDurationMs = Math.max(actualDurationSeconds * 1000, 1000);
  const optimalDurationMs = Math.max(optimalDurationSeconds * 1000, 1000);
  const maxSectionDuration = Math.max(actualDurationMs, optimalDurationMs);
  
  // Create upsampled data for smooth animation
  const { upsampledTimeVector, upsampledActualData, upsampledOptimalData } = 
    createUpsampledAnimationData(sectionData, actualRelativeTime, optimalRelativeTime);
  
  return {
    actualRelativeTime,
    optimalRelativeTime,
    maxSectionDuration,
    sectionData,
    upsampledTimeVector,
    upsampledActualData,
    upsampledOptimalData
  };
};

/**
 * Get current animation data using upsampled data for smooth 20fps interpolation
 */
export const getSmoothAnimationData = (
  frame: number,
  timing: AnimationTiming
): CurrentAnimationFrame => {
  // Input validation
  if (!Number.isFinite(frame) || frame < 0 || frame > 100) {
    frame = Math.max(0, Math.min(100, frame || 0));
  }
  
  const progress = frame / 100;
  
  if (timing.upsampledTimeVector.length === 0 || timing.upsampledActualData.length === 0) {
    return {
      actualIndex: 0,
      optimalIndex: 0,
      actual: { lat_accel: 0, long_accel: 0, speed: 0, optimal_speed: 0, time: 0, optimal_time: 0, latitude: 0, longitude: 0 },
      optimal: { lat_accel: 0, long_accel: 0, speed: 0, time: 0, latitude: 0, longitude: 0 }
    };
  }
  
  // Calculate elapsed time in animation (0 to max duration)
  const elapsedAnimationTime = progress * (timing.maxSectionDuration / 1000);

  // Interpolate between upsampled data points for smooth motion
  const rawActualIndex = elapsedAnimationTime / ANIMATION_TIME_STEP;
  const actualIndex = Math.min(Math.floor(rawActualIndex), timing.upsampledActualData.length - 2);
  const actualFrac = rawActualIndex - actualIndex;

  const rawOptimalIndex = elapsedAnimationTime / ANIMATION_TIME_STEP;
  const optimalIndex = Math.min(Math.floor(rawOptimalIndex), timing.upsampledOptimalData.length - 2);
  const optimalFrac = rawOptimalIndex - optimalIndex;

  const actualIdx = Math.max(0, actualIndex);
  const actualP1 = timing.upsampledActualData[actualIdx];
  const actualP2 = timing.upsampledActualData[Math.min(actualIdx + 1, timing.upsampledActualData.length - 1)];
  const clampedActualFrac = Math.max(0, Math.min(1, actualFrac));

  const optimalIdx = Math.max(0, optimalIndex);
  const optimalP1 = timing.upsampledOptimalData[optimalIdx];
  const optimalP2 = timing.upsampledOptimalData[Math.min(optimalIdx + 1, timing.upsampledOptimalData.length - 1)];
  const clampedOptimalFrac = Math.max(0, Math.min(1, optimalFrac));

  return {
    actualIndex: actualIdx,
    optimalIndex: optimalIdx,
    actual: {
      lat_accel: lerp(actualP1.lat_accel, actualP2.lat_accel, clampedActualFrac),
      long_accel: lerp(actualP1.long_accel, actualP2.long_accel, clampedActualFrac),
      speed: lerp(actualP1.speed, actualP2.speed, clampedActualFrac),
      optimal_speed: lerp(actualP1.optimal_speed, actualP2.optimal_speed, clampedActualFrac),
      time: lerp(actualP1.time, actualP2.time, clampedActualFrac),
      optimal_time: lerp(actualP1.optimal_time, actualP2.optimal_time, clampedActualFrac),
      latitude: lerp(actualP1.latitude, actualP2.latitude, clampedActualFrac),
      longitude: lerp(actualP1.longitude, actualP2.longitude, clampedActualFrac)
    },
    optimal: {
      lat_accel: lerp(optimalP1.lat_accel, optimalP2.lat_accel, clampedOptimalFrac),
      long_accel: lerp(optimalP1.long_accel, optimalP2.long_accel, clampedOptimalFrac),
      speed: lerp(optimalP1.speed, optimalP2.speed, clampedOptimalFrac),
      time: lerp(optimalP1.time, optimalP2.time, clampedOptimalFrac),
      latitude: lerp(optimalP1.latitude, optimalP2.latitude, clampedOptimalFrac),
      longitude: lerp(optimalP1.longitude, optimalP2.longitude, clampedOptimalFrac)
    }
  };
};
